import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LifeBuoy, MessageSquarePlus, Loader2, CheckCircle2, ListChecks, Phone } from "lucide-react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import useAuthStore from "../store/authStore";

const CATEGORIES = [
  { slug: "PICKUP_ISSUE",     label: "Pickup issue" },
  { slug: "DELIVERY_ISSUE",   label: "Delivery issue" },
  { slug: "PAYMENT_ISSUE",    label: "Payment issue" },
  { slug: "PARCEL_DAMAGE",    label: "Parcel damage" },
  { slug: "WRONG_ADDRESS",    label: "Wrong address" },
  { slug: "REFUND_REQUEST",   label: "Refund request" },
  { slug: "AGENT_ISSUE",      label: "Agent issue" },
  { slug: "GENERAL_QUESTION", label: "General question" },
  { slug: "OTHER",            label: "Other" },
];

const MIN = 10;
const MAX = 1000;

export default function CustomerSupport() {
  const { user } = useAuthStore();
  const location = useLocation();
  const prefillOrder = location.state?.orderId || "";

  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [orderId, setOrderId] = useState(prefillOrder);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const charCount = message.length;
  const trimmedLen = message.trim().length;
  const tooShort = trimmedLen > 0 && trimmedLen < MIN;
  const tooLong = charCount >= MAX;
  const counterTone =
    tooLong || tooShort
      ? "text-rose-600 font-semibold"
      : trimmedLen >= MIN
      ? "text-emerald-600"
      : "text-slate-400";

  const canSubmit = !!category && trimmedLen >= MIN && trimmedLen <= MAX && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/customer/support/tickets", {
        category,
        message: message.trim(),
        order_id: orderId.trim() || undefined,
      });
      setResult(data);
      toast.success(`Ticket ${data.ticket_id} created`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
        ? detail[0].msg
        : "Could not submit your request. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setCategory("");
    setMessage("");
    setOrderId("");
    setResult(null);
  };

  // ─── Success view ───────────────────────────────────────────────
  if (result) {
    return (
      <div className="max-w-2xl mx-auto py-10 px-4" data-testid="support-success">
        <div className="bg-white rounded-3xl border border-emerald-200 shadow-sm p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="font-['Outfit'] text-2xl font-bold mt-4 text-slate-900">
            Thanks for contacting PickNGo Support.
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Your ticket ID is{" "}
            <span
              className="inline-block px-2.5 py-0.5 rounded-md bg-orange-50 text-orange-700 font-mono font-semibold border border-orange-200"
              data-testid="support-success-ticket-id"
            >
              {result.ticket_id}
            </span>
          </p>
          <p className="text-slate-700 mt-4 leading-relaxed" data-testid="support-success-auto-reply">
            {result.auto_reply}
          </p>
          <p className="text-xs text-slate-500 mt-6 inline-flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5" />
            For urgent pickup or delivery issues, please also contact us through WhatsApp.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <Link to="/dashboard">
              <Button variant="outline" data-testid="support-view-history-btn">
                <ListChecks className="h-4 w-4 mr-2" />
                View my support history
              </Button>
            </Link>
            <Button
              onClick={resetForm}
              className="bg-[#FF6B00] hover:bg-orange-600 text-white"
              data-testid="support-submit-another-btn"
            >
              <MessageSquarePlus className="h-4 w-4 mr-2" />
              Submit another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Form view ──────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto py-8 px-4" data-testid="support-page">
      <div className="mb-6">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <LifeBuoy className="h-7 w-7 text-[#FF6B00]" /> Help & Support
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Tell us what's going on. We'll get back to {user?.email || "you"} via email shortly.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5"
      >
        {/* Category */}
        <div>
          <label className="text-sm font-semibold text-slate-800 mb-1.5 flex items-center gap-1">
            Category <span className="text-rose-600">*</span>
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            required
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/40"
            data-testid="support-category-select"
          >
            <option value="">Select a category…</option>
            {CATEGORIES.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Order ID (optional) */}
        <div>
          <label className="text-sm font-semibold text-slate-800 mb-1.5 block">
            Related Order ID <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="e.g. PKG-12345 or your order ID"
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/40"
            data-testid="support-order-id-input"
          />
        </div>

        {/* Message */}
        <div>
          <label className="text-sm font-semibold text-slate-800 mb-1.5 flex items-center gap-1">
            How can we help? <span className="text-rose-600">*</span>
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
            maxLength={MAX}
            rows={6}
            placeholder="Describe your issue in at least 10 characters…"
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/40 resize-y"
            data-testid="support-message-textarea"
          />
          <div className="flex items-center justify-between mt-1.5">
            <span
              className={`text-xs ${tooShort ? "text-rose-600" : "text-slate-400"}`}
              data-testid="support-message-helper"
            >
              {tooShort
                ? "Please describe your issue in at least 10 characters."
                : "Provide as much detail as possible."}
            </span>
            <span className={`text-xs tabular-nums ${counterTone}`} data-testid="support-char-counter">
              {charCount} / {MAX}
            </span>
          </div>
        </div>

        <Button
          type="submit"
          disabled={!canSubmit}
          className="w-full bg-[#FF6B00] hover:bg-orange-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="support-submit-btn"
        >
          {submitting ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
          ) : (
            <>Submit support request</>
          )}
        </Button>
      </form>

      <p className="text-xs text-slate-400 mt-4 text-center">
        Looking for an instant answer?{" "}
        <Link to="/dashboard/help" className="text-[#FF6B00] hover:underline">
          Chat with our AI assistant
        </Link>
        .
      </p>
    </div>
  );
}
